/* eslint-disable react/no-array-index-key */
import {
  ArrayUtils,
  asQueryString,
  ConfigObjectType,
  DateUtils,
  DlMuiButton,
  IconHeaderWithSearch,
  LightSplitLayout,
  LightSplitLayoutProps,
  StringUtils,
  useCurrentLocale,
  useLgOrLarger,
  useSize,
  ExportUtils,
  NoDataMessageWithIcon,
} from '@wallaby-ui/core';
import {
  AG_GRID_COMPATIBILITY,
  CHART_MONTH_TICKFORMAT,
  ChartColor,
  clampPageNumber,
  DEFAULT_PAGE_SIZE,
  Formatters,
  getDefaultMTOptions,
  getLineChartItems,
  getTableMaxHeight,
  HaidiEncounterLinkFormatter,
  HaidiHaiStateFormatter,
  HaidiHaiTypeFormatter,
  HaidiSchema,
  JustTooltipFormatter,
  LightChart,
  LightChartImageDownloadProps,
  LightChartLayout,
  LightChartLegend2,
  LightLineChartTrace,
  LightSelectedFilters,
  MaterialTableWrapper,
  ProgressBarFormatter,
  TableSorting,
  TooltipWrapper,
  useSnomedLabels,
} from '@wallaby-ui/document';
import {parseISO} from 'date-fns';
import {get} from 'lodash';
import {Column} from 'material-table';
import React, {useMemo, useCallback} from 'react';
import {Link} from 'react-router-dom';

import {CodeToTranslationsMap, useStandardTypes} from '../encounter/hai/useStandardTypes';
import {HaiListLinkFilters} from '../hai';
import {filterLegendByHaiState, markChartDataLegendByHaiState} from './reportLegendUtils';
import {useChartHiddenMap} from '../../common/useChartHiddenMap';

export type DepartmentReportData = HaidiSchema.components['schemas']['HaiDepartmentReportDto'];
type WithTotal<T> = T & {isTotalRow?: boolean};

// haisByType table
type HaisByTypeRowType = DepartmentReportData['haiByTypeDetailItems'][0];
type HaisByTypeRowTypeWithTotal = WithTotal<HaisByTypeRowType> & {measureCount: number};
const EMPTY = '-';

function useHaisByTypeColumns(
  formatters: Formatters,
  defaultSort: TableSorting,
  config: ConfigObjectType,
  filters: any
): Column<HaisByTypeRowTypeWithTotal>[] {
  const {t, tEnum, formatFloat} = formatters;
  const {standardTypeTranslations} = useStandardTypes({config});
  const getLabel: (v: {haiType: string}) => string =
    filters.report.typeCategory === 'HAI_TYPE'
      ? (v) =>
          (() => {
            let type = tEnum('HaiTypeEnum', v.haiType);
            if (v.haiType === 'BSI') {
              type = type.replace(/\s*\([^)]*\)/g, '').trim();
            }
            return `${type}`;
          })()
      : (v) => (v.haiType == null ? t('reports.uncategorized') : `${v.haiType} - ${standardTypeTranslations[v.haiType]}` ?? v.haiType);

  // const xlOrLarger = useXlOrLarger();
  const columns: Column<HaisByTypeRowTypeWithTotal>[] = [
    {
      field: 'haiType',
      title: t('reports.haiCountPerType.haiType'),
      render: (data) => {
        const haiTypeString = data.isTotalRow ? t('reports.totalRow') : getLabel(data);
        return (
          <JustTooltipFormatter
            {...AG_GRID_COMPATIBILITY}
            value={haiTypeString ?? EMPTY}
            tooltipOverride={haiTypeString}
            singleLine
            bold={data.isTotalRow}
          />
        );
      },
      sorting: true,
    },
    {
      field: 'haiCount',
      title: t('reports.haiCountPerType.haiCount'),
      render: (data) => {
        const haiCountString = formatFloat(data.measureCount);
        return (
          <JustTooltipFormatter
            {...AG_GRID_COMPATIBILITY}
            value={haiCountString ?? EMPTY}
            tooltipOverride={haiCountString}
            bold={data.isTotalRow}
          />
        );
      },
      sorting: true,
    },
    {
      field: 'haiHospitalizationPercentage',
      title: t('reports.haiCountPerType.hospWithHaiPercentage'),
      render: (data) => {
        const percent = data.haiHospitalizationPercentage;
        const percentString = `${formatFloat(percent)} %`;
        return (
          <ProgressBarFormatter
            {...AG_GRID_COMPATIBILITY}
            value={percentString ?? EMPTY}
            tooltipOverride={percentString}
            bold={data.isTotalRow}
            progressPercent={percent}
            style={{maxWidth: '150px'}}
          />
        );
      },
      sorting: true,
    },
    {
      field: 'haiCountPer1000HospitalizationDays',
      title: t('reports.haiCountPerType.haiCountPer1000CareDays'),
      render: (data) => {
        const haiCountPer1000CareDaysString = formatFloat(data.haiCountPer1000HospitalizationDays);
        return (
          <JustTooltipFormatter
            {...AG_GRID_COMPATIBILITY}
            value={haiCountPer1000CareDaysString ?? EMPTY}
            tooltipOverride={haiCountPer1000CareDaysString}
            bold={data.isTotalRow}
          />
        );
      },
      sorting: true,
    },
    {
      field: 'deathCausedByHaiCount',
      title: t('reports.haiCountPerType.deathDueToHaiCount'),
      render: (data) => {
        const deathDueToHaiCountString = formatFloat(data.deathCausedByHaiCount);
        return (
          <JustTooltipFormatter
            {...AG_GRID_COMPATIBILITY}
            value={deathDueToHaiCountString ?? EMPTY}
            tooltipOverride={deathDueToHaiCountString}
            bold={data.isTotalRow}
          />
        );
      },
      sorting: true,
    },
  ];
  return columns.map((v) => ({
    ...v,
    sorting: v.sorting ?? false,
    customSort:
      v.customSort ??
      ((row1, row2, _type) => {
        const isSortAscending = defaultSort.direction === 'asc';
        if (row1.isTotalRow) return isSortAscending ? -1 : 1;
        if (row2.isTotalRow) return isSortAscending ? 1 : -1;
        const key = (row: HaisByTypeRowTypeWithTotal) => get(row, v.field ?? '');
        return StringUtils.compare(key(row1), key(row2));
      }),
    ...(v.field === defaultSort.field ? {defaultSort: defaultSort.direction ?? undefined} : {}),
  }));
}

// measure table
type HaiMeasureStruct = {
  hospCount?: number;
  careDays?: number;
  avgHospDuration?: number;
  avgHospDurationHai?: number;
  avgHospDurationNoHai?: number;
  deathCausedByHaiCount?: number;
};
type MeasureRowType = {
  name: string;
  value: number;
  precision?: number;
  maxPrecision?: number;
};

function useMeasureColumns(formatters: Formatters, defaultSort: TableSorting): Column<MeasureRowType>[] {
  const {t, formatFloat, formatFloatExact} = formatters;
  // const xlOrLarger = useXlOrLarger();
  const columns: Column<MeasureRowType>[] = [
    {
      field: 'name',
      title: t('reports.measure.name'),
      render: (data) => {
        const nameString = t(`reports.measure.${data.name}`);
        return <JustTooltipFormatter {...AG_GRID_COMPATIBILITY} value={nameString ?? EMPTY} tooltipOverride={nameString} />;
      },
      sorting: true,
      customSort: (row1, row2) => {
        const key = (row: MeasureRowType) => t(`reports.measure.${row.name}`);
        return StringUtils.compare(key(row1), key(row2));
      },
    },
    {
      field: 'value',
      title: t('reports.measure.value'),
      render: (data) => {
        const dataPrecision = data.name === 'careDays' ? (data.precision = 1) : data.precision;
        const valueString =
          data.precision != null ? formatFloatExact(data.value, dataPrecision) : formatFloat(data.value, data.maxPrecision);
        return <JustTooltipFormatter {...AG_GRID_COMPATIBILITY} value={valueString ?? EMPTY} tooltipOverride={valueString} />;
      },
      sorting: true,
    },
  ];
  return columns.map((v) => ({
    ...v,
    sorting: v.sorting ?? false,
    customSort:
      v.customSort ??
      ((row1, row2, _type) => {
        const key = (row: MeasureRowType) => get(row, v.field ?? '');
        return StringUtils.compare(key(row1), key(row2));
      }),
    ...(v.field === defaultSort.field ? {defaultSort: defaultSort.direction ?? undefined} : {}),
  }));
}

// hais table
type HaisRowType = DepartmentReportData['lastHais'][0];

export function useDepartmentReportHaisColumns(
  snomedLabels: Record<string, string>,
  formatters: Formatters,
  defaultSort: TableSorting,
  hasPathogens: boolean,
  standardTypeTranslations: CodeToTranslationsMap
): Column<HaisRowType>[] {
  const {t, tEnum, formatDate} = formatters;
  const locale = useCurrentLocale();
  const lgOrLarger = useLgOrLarger();
  const {getBySize} = useSize({
    bools: [lgOrLarger],
    sizes: ['sm', 'lg'] as const,
  });
  const isPathogensDescending = defaultSort.field === 'pathogens' && defaultSort.direction === 'desc';
  const sortPathogens = (pathogens: number[]) => {
    const sortedPathogens = ArrayUtils.sortedByLocaleString(pathogens, (v) => snomedLabels[v] ?? v, locale);
    return isPathogensDescending ? sortedPathogens.reverse() : sortedPathogens;
  };
  const columns: Column<HaisRowType>[] = [
    {
      field: 'patientName',
      title: t('dashboard.table.name'),
      render: (data) => (
        <HaidiEncounterLinkFormatter
          {...AG_GRID_COMPATIBILITY}
          data={data}
          value={`${data.patientName} (${data.patientIdentificationNumber})`}
        />
      ),
      sorting: true,
      customSort: (a, b) => {
        const compareA = StringUtils.localeCompare(a.patientName, b.patientName, locale);
        if (compareA !== 0) return compareA;
        const compareB = StringUtils.localeCompare(a.patientIdentificationNumber ?? '', b.patientIdentificationNumber ?? '', locale);
        return compareB;
      },
    },
    {
      field: 'haiDate',
      title: t('reports.lastHais.date'),
      render: (data) => <JustTooltipFormatter {...AG_GRID_COMPATIBILITY} value={formatDate(data?.haiDate ?? '')} />,
      width: '96px',
      sorting: true,
    },
    {
      field: 'type',
      title: t('reports.lastHais.haiType'),
      render: (data) => {
        const dataWithTranslation = {
          ...data,
          standardTypeTranslation: data.standardTypeCodeLabel ? standardTypeTranslations[data.standardTypeCodeLabel] : undefined,
        };
        return <HaidiHaiTypeFormatter {...AG_GRID_COMPATIBILITY} value={data?.type} data={dataWithTranslation} noWrap />;
      },
      width: getBySize({
        lg: '158px',
        sm: '90px',
      }),
      sorting: true,
      /* customSort: (a, b) => {
        const key = (v: HaisRowType) => tEnum('HaiTypeEnum', v?.type);
        return StringUtils.localeCompare(key(a), key(b), locale);
      }, */
    },
    {
      field: 'haiState',
      title: t('reports.lastHais.haiState'),
      render: (data) => {
        const {haiState} = data;
        return <HaidiHaiStateFormatter {...AG_GRID_COMPATIBILITY} data={{state: haiState}} value={haiState} />;
      },
      width: getBySize({
        lg: '145px',
        sm: '90px',
      }),
      sorting: true,
      /* customSort: (a, b) => {
        const key = (v: HaisRowType) => tEnum('State', v.haiState);
        return StringUtils.localeCompare(key(a), key(b), locale);
      }, */
    },
    ...(hasPathogens
      ? [
          {
            field: 'pathogens',
            title: t('reports.lastHais.pathogens'),
            render: (data: HaisRowType) => {
              return (
                <div style={{display: 'flex', flexDirection: 'column', alignItems: 'stretch'}}>
                  {sortPathogens(data.pathogens ?? []).map((v, i) => {
                    const pathogenLabel = snomedLabels[v] ?? v;
                    return (
                      <TooltipWrapper
                        key={i}
                        tooltip={pathogenLabel}
                        wrapperStyle={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {pathogenLabel}
                      </TooltipWrapper>
                    );
                  })}
                </div>
              );
            },
            width: getBySize({
              lg: '',
              sm: '90px',
            }),
            sorting: true,
            customSort: (a, b) => {
              const key = (row: HaisRowType) => {
                const firstSortedPathogen = sortPathogens(row.pathogens ?? [])[0];
                return snomedLabels[firstSortedPathogen] ?? firstSortedPathogen ?? '';
              };
              const keyA = key(a);
              const keyB = key(b);
              const emptyComparison = StringUtils.compareIfEmpty(keyA === '', keyB === '', isPathogensDescending);
              if (emptyComparison !== 0) return emptyComparison;
              return StringUtils.localeCompare(keyA, keyB, locale);
            },
          },
        ]
      : []),
  ];
  return columns.map((v) => ({
    ...v,
    sorting: v.sorting ?? false,
    customSort:
      v.customSort ??
      ((row1, row2, _type) => {
        const key = (row: HaisRowType) => get(row, v.field ?? '');
        return StringUtils.compare(key(row1), key(row2));
      }),
    ...(v.field === defaultSort.field ? {defaultSort: defaultSort.direction ?? undefined} : {}),
  }));
}

type Props = {
  config: ConfigObjectType;
  muiStyles: any;
  storageIdPrefix: string;
  filters: any;
  changeFilters: (newFilters: any) => void;
  hasTopSorting: boolean;
  hasHaisSorting: boolean;
  handleResetTopSorting: () => void;
  handleResetHaisSorting: () => void;
  hasFilters: boolean;
  handleResetFilters: () => void;
  formatters: Formatters;
  isLoading: boolean;
  reportData: DepartmentReportData | undefined;
};
export const DepartmentReportTablesAndChart: React.FC<Props> = (props) => {
  const {
    config,
    muiStyles,
    storageIdPrefix: _,
    filters,
    changeFilters,
    hasTopSorting,
    hasHaisSorting,
    handleResetTopSorting,
    hasFilters,
    handleResetFilters,
    handleResetHaisSorting,
    formatters,
    isLoading,
    reportData,
  } = props;
  const {tEnum, t} = formatters;
  const haisByTypeColumns = useHaisByTypeColumns(formatters, filters.haisByTypeSort, config, filters);
  const {standardTypeTranslations} = useStandardTypes({config});
  const haisByTypeData: HaisByTypeRowTypeWithTotal[] = useMemo(() => {
    const totalRow: HaisByTypeRowTypeWithTotal = reportData?.haiByTypeDetailTotal ?? {
      haiType: '',
      hospCount: 0,
      careDays: 0,
      deathCausedByHaiCount: 0,
      haiCount: 0,
      haiHospitalizationPercentage: 0,
      haiCountPer1000HospitalizationDays: 0,
      haiCountPer1000HospitalizationDaysLastYear: 0,
      isTotal: true,
      measureCount: 0,
    };
    totalRow.isTotalRow = true;
    return [totalRow, ...(reportData?.haiByTypeDetailItems ?? [])];
  }, [reportData]);
  const measureColumns = useMeasureColumns(formatters, filters.measureSort);
  const measureRawData: HaiMeasureStruct = useMemo(() => reportData?.departmentReportMetrics ?? {}, [reportData]);
  const measureData = useMemo(() => {
    const acc: MeasureRowType[] = [
      {name: 'hospCount', value: measureRawData.hospCount ?? 0, precision: 0},
      {name: 'careDays', value: measureRawData.careDays ?? 0, precision: 0},
      {name: 'avgHospDuration', value: measureRawData.avgHospDuration ?? 0, precision: 2},
      {name: 'avgHospDurationHai', value: measureRawData.avgHospDurationHai ?? 0, precision: 2},
      {name: 'avgHospDurationNoHai', value: measureRawData.avgHospDurationNoHai ?? 0, precision: 2},
      // {name: 'deathCausedByHaiCount', value: measureRawData.deathCausedByHaiCount ?? 0, precision: 0},
    ];
    return acc;
  }, [measureRawData]);
  const {snomedLabels} = useSnomedLabels(config);
  const haisColumns = useDepartmentReportHaisColumns(snomedLabels, formatters, filters.haisSort, false, standardTypeTranslations);
  // chart
  const chartLayout: LightChartLayout = useMemo(() => {
    // const [chartStartDate, chartEndDate] = chartRange;
    // const chartEndDateNextMidnight = DateUtils.nextMidnightIsoString(chartEndDate ?? '');
    return {
      margin: {
        b: 42,
      },
      xaxis: {
        type: 'date',
        tickformat: CHART_MONTH_TICKFORMAT,
        automargin: true,
      },
      yaxis: {
        rangemode: 'tozero',
        automargin: true,
        fixedrange: true,
      },
      height: 350,
      showlegend: false,
    };
  }, []);
  const chartImageDownload: LightChartImageDownloadProps = {
    showlegend: true,
    title: {
      text: t('reports.haiCountByMonth'),
    },
  };
  const {hiddenMap, onSetHiddenMap, applyHidden} = useChartHiddenMap();
  const chartColors: ChartColor[] = useMemo(() => {
    return applyHidden([
      {
        id: 'YES',
        color: '#FF8717',
        label: tEnum('State', 'YES'),
      },
      {
        id: 'COL',
        color: '#6FA9FF',
        label: tEnum('State', 'COL'),
      },
    ]);
  }, [tEnum, applyHidden]);
  const parseChartData = (v: DepartmentReportData['confirmedHaisByMonth'][0]) => ({
    timestamp: DateUtils.fromReverseMonthFormat(v.month ?? '').toISOString(),
    count: v.count ?? 0,
    state: v.state,
  });
  // Month buckets may arrive out of chronological order; sort ascending so the line doesn't jump backwards.
  const byTimestampAsc = (a: {timestamp: string}, b: {timestamp: string}) =>
    new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime();
  const unboundChartData = ArrayUtils.zip(
    (reportData?.confirmedHaisByMonth ?? []).map(parseChartData).sort(byTimestampAsc),
    (reportData?.colonizedHaisByMonth ?? []).map(parseChartData).sort(byTimestampAsc)
  );
  const allCountsZero = unboundChartData.every((arr) => arr.every((obj) => obj?.count === 0));
  const legendItems = useMemo(() => filterLegendByHaiState(chartColors, filters.report.haiState), [chartColors, filters.report.haiState]);
  const dataChartColors = useMemo(
    () => markChartDataLegendByHaiState(chartColors, filters.report.haiState),
    [chartColors, filters.report.haiState]
  );
  const chartData: LightLineChartTrace[] = useMemo(() => {
    const {formatFloat} = formatters;
    return getLineChartItems(allCountsZero ? [] : unboundChartData, dataChartColors, (v, i) => ({
      x: v[i]?.timestamp ?? '',
      y: v[i]?.count ?? 0,
      tooltip: `${DateUtils.toReverseMonthFormat(parseISO(v[i]?.timestamp ?? ''))}<br><b>${dataChartColors[i].label}</b>: ${formatFloat(
        v[i]?.count ?? 0
      )}`,
    })).map((v) => ({...v, cliponaxis: false}));
  }, [unboundChartData, dataChartColors, formatters]); // eslint-disable-line react-hooks/exhaustive-deps
  // hais
  const lastHaisData = useMemo(() => reportData?.lastHais ?? [], [reportData]);
  const haisLinkQuery = useMemo(() => {
    let states = ['YES', 'COL'];
    if (filters.report.haiState !== 'ALL') {
      states = [filters.report.haiState];
    }
    const acc: HaiListLinkFilters = {
      detectionDate: {
        startDate: filters.report.dateFrom,
        endDate: filters.report.dateTo,
      },
      states,
    };
    if (filters.report.organizationIds?.length) {
      acc.organizationIds = filters.report.organizationIds; // NOTE: HaisList stores empty value as null | undefined
    }
    return {hais: acc};
  }, [filters]);
  // split
  /* const splitA: LightSplitLayoutProps = {
    storageId: '/LightSplitLayout.departmentReport.A',
    initSizes: [50, 20],
    minSizes: [100, 100],
  }; */
  const splitB: LightSplitLayoutProps = {
    storageId: '/LightSplitLayout.departmentReport.B',
    initSizes: [50, 50],
    minSizes: [100, 100],
  };
  const currentLocale = useCurrentLocale();
  const downloadPdfExport = useCallback(() => {
    ExportUtils.handleExportPdf({url: config.service.url.departmentReportPdfExport, filters: filters.report, locale: currentLocale});
  }, [config.service.url.departmentReportPdfExport, currentLocale, filters.report]);
  const downloadXlsxExport = useCallback(() => {
    ExportUtils.handleExportXlsx({
      url: config.service.url.haiXlsxExport.replace('{reportName}', 'HAI_DEPARTMENT_REPORT'),
      filters: filters.report,
      locale: currentLocale,
    });
  }, [config.service.url.haiXlsxExport, currentLocale, filters]);

  const noDataComponent = useMemo(() => <NoDataMessageWithIcon message={'chart.emptyData'} isHaidi />, []);

  return (
    <React.Fragment key='DepartmentReportTablesAndChart'>
      <div css={muiStyles.contentBox}>
        <IconHeaderWithSearch
          iconName={'listIconMultiColor'}
          label={'reports.haiCountPerType'}
          useFixedViewBox='0 -1 16 14'
          iconCss={muiStyles.headerIcon}
        >
          <LightSelectedFilters
            hasFilters={hasFilters}
            handleResetFilters={handleResetFilters}
            hasSorting={hasTopSorting}
            handleResetSorting={handleResetTopSorting}
            hasXlsxExport
            handleXlsxExport={() => downloadXlsxExport()}
            hasPdfExport
            handlePdfExport={() => downloadPdfExport()}
          />
        </IconHeaderWithSearch>
        <div css={muiStyles.horizontalSplit}>
          <MaterialTableWrapper
            style={{flex: 5, maxHeight: getTableMaxHeight(15)}}
            options={{
              ...getDefaultMTOptions(DEFAULT_PAGE_SIZE),
              paging: false,
            }}
            variant='big'
            totalRow='top'
            useMaxHeight
            columns={haisByTypeColumns}
            data={haisByTypeData}
            isLoading={isLoading}
            onOrderChange={(colIndex, direction) => {
              const columnId = haisByTypeColumns[colIndex].field as string;
              changeFilters({
                haisByTypeSort: {
                  field: columnId,
                  direction,
                },
              });
            }}
            controlledSort={(() => {
              return filters.haisByTypeSort;
            })()}
          />
          <MaterialTableWrapper
            style={{flex: 2}}
            options={{
              ...getDefaultMTOptions(DEFAULT_PAGE_SIZE),
              paging: false,
            }}
            variant='big'
            useMaxHeight
            columns={measureColumns}
            data={measureData}
            isLoading={isLoading}
            onOrderChange={(colIndex, direction) => {
              const columnId = measureColumns[colIndex].field as string;
              changeFilters({
                measureSort: {
                  field: columnId,
                  direction,
                },
              });
            }}
            controlledSort={(() => {
              return filters.measureSort;
            })()}
          />
        </div>
      </div>
      <LightSplitLayout {...splitB} style={{height: 'auto'}}>
        <div css={muiStyles.contentBox} style={{height: 'auto'}}>
          <IconHeaderWithSearch
            iconName={'listIconMultiColor'}
            label={'reports.haiCountByMonth'}
            useFixedViewBox='0 -1 16 14'
            iconCss={muiStyles.headerIcon}
          >
            <LightChartLegend2 items={legendItems} hiddenMap={hiddenMap} onSetHiddenMap={onSetHiddenMap} />
          </IconHeaderWithSearch>
          <LightChart
            key={JSON.stringify(chartLayout.xaxis.range)}
            data={chartData}
            layout={chartLayout}
            config={{}}
            imageDownload={chartImageDownload}
            noDataComponent={noDataComponent}
          />
        </div>
        <div css={muiStyles.contentBox}>
          <IconHeaderWithSearch
            iconName={'listIconMultiColor'}
            label={'reports.lastHais'}
            useFixedViewBox='0 -1 16 14'
            iconCss={muiStyles.headerIcon}
            childrenWrapperStyle={{gap: 6}}
          >
            <LightSelectedFilters hasSorting={hasHaisSorting} handleResetSorting={handleResetHaisSorting} />
            <Link css={muiStyles.showAllLink} tabIndex={0} to={`/hais?${asQueryString(haisLinkQuery)}`}>
              <DlMuiButton
                type='chip-filled'
                title='dashboard.showAll'
                onClick={() => {
                  // noop
                }}
              />
            </Link>
          </IconHeaderWithSearch>
          <MaterialTableWrapper
            options={{
              ...getDefaultMTOptions(DEFAULT_PAGE_SIZE),
              pageSize: filters.haisPaging.pageSize ?? DEFAULT_PAGE_SIZE,
            }}
            variant='big'
            variantPadding='small'
            useMaxHeight
            columns={haisColumns}
            data={lastHaisData}
            isLoading={isLoading}
            controlledPage={filters.haisPaging.page ?? 0}
            onChangePage={(page, pageSize) =>
              changeFilters({
                haisPaging: {
                  ...filters.haisPaging,
                  page,
                  pageSize,
                },
              })
            }
            onChangeRowsPerPage={(pageSize) =>
              changeFilters({
                haisPaging: {
                  ...filters.haisPaging,
                  pageSize,
                  page: clampPageNumber(filters.haisPaging.page, pageSize, lastHaisData.length),
                },
              })
            }
            onOrderChange={(colIndex, direction) => {
              const columnId = haisColumns[colIndex].field as string;
              changeFilters({
                haisSort: {
                  field: columnId,
                  direction,
                },
              });
            }}
            controlledSort={(() => {
              return filters.haisSort;
            })()}
          />
        </div>
      </LightSplitLayout>
    </React.Fragment>
  );
};
